import { useCallback } from 'react';

interface ScrollOptions {
  behavior?: 'smooth' | 'auto';
  block?: 'start' | 'center' | 'end' | 'nearest';
  inline?: 'start' | 'center' | 'end' | 'nearest';
  offset?: number;
}

interface UseAutoScrollReturn {
  scrollToElement: (elementId: string, options?: ScrollOptions) => void;
  scrollToBottom: (containerId?: string, options?: ScrollOptions) => void;
  scrollToTop: (containerId?: string, options?: ScrollOptions) => void;
  scrollToNextSection: (currentSectionId: string, options?: ScrollOptions) => void;
  scrollToContinueButton: (options?: ScrollOptions) => void;
}

const useAutoScroll = (): UseAutoScrollReturn => {
  const scrollToElement = useCallback((elementId: string, options: ScrollOptions = {}) => {
    const element = document.getElementById(elementId);
    if (element) {
      const {
        behavior = 'smooth',
        block = 'center',
        inline = 'nearest',
        offset = 0
      } = options;

      // Calculate position with offset
      const elementRect = element.getBoundingClientRect();
      const container = element.closest('.scrollable-container') || document.documentElement;
      const containerRect = container.getBoundingClientRect();
      
      let targetScrollTop = element.offsetTop - offset;
      
      // If element is in a scrollable container, adjust accordingly
      if (container !== document.documentElement) {
        targetScrollTop = element.offsetTop - container.offsetTop - offset;
      }

      // Scroll to element
      element.scrollIntoView({
        behavior,
        block,
        inline
      });

      // Apply offset if needed
      if (offset > 0) {
        setTimeout(() => {
          if (container === document.documentElement) {
            window.scrollTo({
              top: targetScrollTop,
              behavior: 'smooth'
            });
          } else {
            container.scrollTop = targetScrollTop;
          }
        }, 100);
      }
    }
  }, []);

  const scrollToBottom = useCallback((containerId?: string, options: ScrollOptions = {}) => {
    const {
      behavior = 'smooth',
      offset = 0
    } = options;

    if (containerId) {
      const container = document.getElementById(containerId);
      if (container) {
        container.scrollTo({
          top: container.scrollHeight - offset,
          behavior
        });
      }
    } else {
      // Scroll to bottom of page
      window.scrollTo({
        top: document.documentElement.scrollHeight - offset,
        behavior
      });
    }
  }, []);

  const scrollToTop = useCallback((containerId?: string, options: ScrollOptions = {}) => {
    const {
      behavior = 'smooth',
      offset = 0
    } = options;

    if (containerId) {
      const container = document.getElementById(containerId);
      if (container) {
        container.scrollTo({
          top: offset,
          behavior
        });
      }
    } else {
      // Scroll to top of page
      window.scrollTo({
        top: offset,
        behavior
      });
    }
  }, []);

  const scrollToNextSection = useCallback((currentSectionId: string, options: ScrollOptions = {}) => {
    const currentSection = document.getElementById(currentSectionId);
    if (currentSection) {
      const nextSection = currentSection.nextElementSibling;
      if (nextSection && nextSection.id) {
        scrollToElement(nextSection.id, options);
      } else {
        // If no next section, scroll to bottom
        scrollToBottom(undefined, options);
      }
    }
  }, [scrollToElement, scrollToBottom]);

  const scrollToContinueButton = useCallback((options: ScrollOptions = {}) => {
    // Look for common continue button selectors
    const continueButtonSelectors = [
      'button:contains("Continue")',
      'button:contains("Next")',
      'button:contains("Proceed")',
      'button:contains("Set Up")',
      'button:contains("Confirm")',
      '.continue-button',
      '.next-button',
      '[data-testid="continue-button"]'
    ];

    for (const selector of continueButtonSelectors) {
      try {
        const button = document.querySelector(selector);
        if (button) {
          button.scrollIntoView({
            behavior: options.behavior || 'smooth',
            block: options.block || 'center',
            inline: options.inline || 'nearest'
          });
          return;
        }
      } catch (e) {
        // Continue to next selector if current one fails
        continue;
      }
    }

    // Fallback: scroll to bottom
    scrollToBottom(undefined, options);
  }, [scrollToBottom]);



  return {
    scrollToElement,
    scrollToBottom,
    scrollToTop,
    scrollToNextSection,
    scrollToContinueButton
  };
};

export default useAutoScroll;
